/**
 * SlidePanel
 *
 * Reusable slide-in panel from the right side of the screen.
 * Glassmorphism styling, backdrop click to close, Escape key support,
 * and body scroll lock when open.
 *
 * @module components/ui/SlidePanel
 */

import React, { useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { X } from 'lucide-react';

export default function SlidePanel({ isOpen, onClose, title, width = '400px', children }) {
  const panelRef = useRef(null);

  // Body scroll lock
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  // Escape key handler
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  // Focus trap: focus the panel when it opens
  useEffect(() => {
    if (isOpen && panelRef.current) {
      panelRef.current.focus();
    }
  }, [isOpen]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        style={{ zIndex: 1300 }}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={`fixed top-0 right-0 h-full bg-black/80 backdrop-blur-xl border-l border-white/10 shadow-glass-lg flex flex-col outline-none transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ zIndex: 1301, width, maxWidth: '90vw' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 shrink-0">
          <h2 className="text-lg font-semibold text-white truncate">{title}</h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-colors"
            aria-label="Close panel"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {children}
        </div>
      </div>
    </>
  );
}

SlidePanel.propTypes = {
  /** Whether the panel is open */
  isOpen: PropTypes.bool.isRequired,
  /** Callback when the panel should close */
  onClose: PropTypes.func.isRequired,
  /** Title displayed in the panel header */
  title: PropTypes.string.isRequired,
  /** Width of the panel (CSS value) */
  width: PropTypes.string,
  /** Panel content */
  children: PropTypes.node,
};
